import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/dates";
import { Pill } from "@/components/evidence/RecordsLog";
import { ClipboardCheck, ClipboardList, Stethoscope, User } from "lucide-react";

const d = (iso) => (iso ? fmtDate(iso) : "—");
const RAG = { green: "text-rag-green bg-rag-green-bg", amber: "text-rag-amber bg-rag-amber-bg", red: "text-rag-red bg-rag-red-bg" };
const FLAG = { audit_red: "Audit: significant gaps", care_plan_overdue: "Care plan review overdue", referral_overdue: "Dental referral overdue" };

function Block({ icon: Icon, title, children, empty }) {
  return (
    <div className="rounded-xl bg-cream px-3 py-2.5 min-w-0">
      <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-brand mb-1"><Icon className="h-3.5 w-3.5" />{title}</div>
      {children || <div className="text-xs text-muted-foreground">{empty}</div>}
    </div>
  );
}

export function ResidentCard({ c }) {
  const key = c.ref.replace(/\s+/g, "-").toLowerCase();
  return (
    <div data-testid={`resident-card-${key}`} className="rounded-2xl bg-cream-card border border-cream-deep p-4">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 font-heading font-bold text-brand"><User className="h-4 w-4" />{c.ref}</div>
        <div className="flex flex-wrap gap-1.5">
          {c.flags.length === 0 && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-rag-green-bg text-rag-green">No open issues</span>}
          {c.flags.map((f) => <span key={f} data-testid={`resident-flag-${key}-${f}`} className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-rag-red-bg text-rag-red">{FLAG[f]}</span>)}
        </div>
      </div>
      <div className="grid sm:grid-cols-3 gap-2">
        <Block icon={ClipboardCheck} title="Resident audit" empty="Not audited yet">
          {c.audit && (
            <div className="text-xs space-y-0.5">
              <div><span className={`font-bold px-1.5 py-0.5 rounded ${RAG[c.audit.rag]}`}>{c.audit.pct}%</span> <span className="text-muted-foreground">· {d(c.audit.date_audited)}{c.audit.status !== "complete" ? " · in progress" : ""}</span></div>
              {c.audit.improvements?.length > 0 && <div className="text-muted-foreground">Improve: {c.audit.improvements.map((x) => x.split(": ")[1] || x).join("; ")}</div>}
              {c.audit.review_date && <div className="text-muted-foreground">Review {d(c.audit.review_date)}{c.audit.person_responsible ? ` · ${c.audit.person_responsible}` : ""}</div>}
            </div>
          )}
        </Block>
        <Block icon={ClipboardList} title="Care plan review" empty="No review logged">
          {c.care_plan && (
            <div className="text-xs space-y-0.5">
              <div>Reviewed {d(c.care_plan.last_reviewed)}{c.care_plan.reviewed_by ? ` · ${c.care_plan.reviewed_by}` : ""}</div>
              <div className="flex items-center gap-1.5">Next due {d(c.care_plan.next_due)} <Pill state={c.care_plan.state} /></div>
            </div>
          )}
        </Block>
        <Block icon={Stethoscope} title="Dental referrals" empty="None logged">
          {c.referrals.length > 0 && (
            <ul className="text-xs space-y-1">
              {c.referrals.slice(0, 3).map((r) => (
                <li key={r.id} className="flex flex-wrap items-center gap-1.5">
                  <span>{d(r.referred_on)}{r.reason ? ` · ${r.reason}` : ""}</span><Pill state={r.state === "overdue" ? "overdue" : r.status} />
                </li>
              ))}
              {c.referrals.length > 3 && <li className="text-muted-foreground">+{c.referrals.length - 3} more</li>}
            </ul>
          )}
        </Block>
      </div>
    </div>
  );
}

export function ResidentTimeline({ residents, readOnly = false }) {
  const [rows, setRows] = useState(residents || []);
  useEffect(() => { if (residents) setRows(residents); }, [residents]);
  useEffect(() => { if (!residents) api.get("/compliance/residents/timeline").then((r) => setRows(r.data)).catch(() => {}); }, [residents]);
  return (
    <section id="resident-timeline" data-testid="resident-timeline" className="mt-8 scroll-mt-24">
      <h4 className="font-heading font-bold text-brand text-sm">Resident timeline</h4>
      <p className="text-xs text-muted-foreground mb-3">{readOnly ? "Each resident's audit, care plan review and dental referrals together." : "Audit, care plan review and referrals on one card per resident — ready for inspector conversations. Matched on the room number / initials you use in each record."}</p>
      {rows.length === 0 ? <p data-testid="resident-timeline-empty" className="text-xs text-muted-foreground rounded-2xl bg-cream-card border border-cream-deep p-5">No resident records yet — audit a resident or log a care plan review to start a timeline.</p>
        : <div className="grid md:grid-cols-2 gap-3">{rows.map((c) => <ResidentCard key={c.ref} c={c} />)}</div>}
    </section>
  );
}
